import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon, { IconProp } from '~components/icon';
import PopOver from '~components/pop-over/PopOver';

// Long enough to cross the gap between the trigger and the portaled popover body, short enough that
// an actual departure still feels immediate.
const CLOSE_DELAY_MS = 200;

type Variant = 'danger' | 'warning' | 'info';

interface ComponentProps {
  /** Unique within the row - used for the popover target id and the data-cy hooks. */
  id: string;
  /**
   * Trigger glyph. The popover header takes its colour from `variant`, not from this.
   *
   * Optional: omit it for a text-only trigger, where `label` carries the whole affordance. The
   * claim items grid's SaH Service cell does that - the value itself is the trigger, marked with
   * the `has-caption` dotted underline, because an icon beside every row of a column whose values
   * ALL have something to say is noise rather than signal.
   */
  icon?: IconProp;
  /**
   * Optional text rendered BEFORE the glyph inside the trigger, turning the trigger from a bare icon
   * into a labelled control - which is what lets a status pill be the trigger (label + a small info
   * glyph advertising that there is something to read) rather than an icon sitting beside one.
   */
  label?: string;
  /**
   * The trigger's structural classes, separate from `triggerClassName`'s colour. Defaulted to the
   * borderless link button the claim-items table uses; a pill caller overrides it so `btn-link`'s
   * link colouring and underline do not fight the badge styling.
   */
  triggerBaseClassName?: string;
  /** Header background only. The trigger is coloured separately - see `triggerClassName`. */
  variant: Variant;
  /**
   * Bootstrap text utility for the trigger button. Deliberately NOT derived from `variant`: the
   * error trigger is a blue info icon beside a red X (the X states the outcome, the info icon
   * advertises that there is something to read), so its trigger and its header differ on purpose.
   */
  triggerClassName: string;
  /**
   * Explicit FontAwesome colour when the glyph must not simply inherit `triggerClassName`. Left
   * unset, the icon renders `fill: currentColor` and picks up the button's text colour.
   */
  iconColor?: string;
  header: string;
  /** One entry per line. An empty array renders nothing at all. */
  messages: string[];
  /**
   * Names the trigger button. Deliberately NOT passed to the popover as well: it renders as a
   * `tooltip`, which takes its accessible name from its own content - labelling it too would make a
   * screen reader read the label instead of the message the user opened it for.
   */
  ariaLabel: string;
  dataCyPrefix: string;
  /**
   * Which side the popover opens on. Defaults to `left`, which is what the claim-items table needs -
   * its trigger sits in the rightmost column, so opening right would push the body off-screen.
   * `autoFlip` still rescues a side with no room, but the default should be the side that fits.
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Milliseconds the pointer must rest on the trigger before the popover opens. Defaults to 0 -
   * open on arrival, which is right for a trigger sitting alone at the end of a row.
   *
   * Set it where the trigger sits mid-row, in a column the pointer crosses on its way somewhere
   * else: without a pause the popover fires on every sweep across the table. Only hover is
   * delayed - focus and tap still open immediately, because both are deliberate.
   */
  openDelay?: number;
}

/**
 * HoverPopover
 *
 * A hover-opened, leave-closed popover carrying one or more short lines of text. Used by the claim
 * items table's `sa_status` cell for the Services Australia error (danger) and the outstanding-flags
 * summary (warning), and by the Support at Home service associations list for a service's
 * department reference detail (info). Shared so those cannot drift apart in behaviour or markup -
 * they differ only by colour, glyph, header and message list.
 *
 * Hover opens and leaving closes, with a grace period on the way out. The grace period is the whole
 * trick: PopOver portals to document.body, so it is NOT a DOM descendant of the trigger, and closing
 * synchronously on mouseleave shut it while the pointer was still travelling towards it - a
 * multi-sentence vendor message could never be read or selected. Leaving SCHEDULES a close; entering
 * either the trigger or the popover body CANCELS it, so the gap is crossable but a real departure
 * still closes.
 *
 * A real <button> rather than a span so focus/blur can mirror hover, which is what keeps the content
 * reachable by keyboard.
 *
 * Touch is served by click-to-toggle, gated on pointer type. Hover does not exist on a touch device,
 * and a hover-only affordance locks those users out entirely - which matters most for an item flagged
 * `sa_further_information_required` ALONE, where ClaimItemRequirements renders nothing and the row
 * would otherwise show a bare warning glyph with no route at all to what it means.
 *
 * The interaction type has to be known because browsers synthesise `mouseenter` before `click` on a
 * tap, so an unconditional click toggle would open (from the synthetic hover) and immediately close
 * again on that same first tap. `touchstart` is what identifies a tap - it fires only for touch, so
 * its presence IS the evidence - with `pointerType` refining it where available. Hover-open is then
 * suppressed for touch, and click toggles only for touch.
 */
const HoverPopover: React.FC<ComponentProps> = ({
  id,
  icon,
  label,
  triggerBaseClassName = 'btn btn-link p-0 border-0 align-baseline',
  variant,
  triggerClassName,
  iconColor,
  header,
  messages,
  ariaLabel,
  dataCyPrefix,
  placement = 'left',
  openDelay = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Whether this popover has EVER been opened. PopOver is not mounted until it has, for two reasons.
  //
  // The load-bearing one: PopOver's open/close effect runs on mount even when closed, taking its
  // `else` branch and scheduling a fade-out `setTimeout` that resolves AFTER the test has finished -
  // a setState outside act(), which setupTests.ts's guard fails the test on. `settle()` cannot catch
  // it either, since that waits 0ms and the fade is longer.
  //
  // The cheap one: a claim-items table renders one of these per row, so a page of 50 rows was mounting
  // 50 portals and 50 timers for popovers nobody had hovered. Once opened it STAYS mounted, so the
  // fade-out on close still plays.
  const [everOpened, setEverOpened] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  // Holds a pending hover-open while the pointer rests on the trigger. Separate from closeTimer:
  // leaving must cancel a scheduled OPEN as well as schedule a close, or the popover appears
  // after the pointer has already moved on.
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  // Whether the interaction in progress came from a touch rather than a mouse. Written by the two
  // handlers below, both of which fire BEFORE the synthetic mouseenter/mousedown/click sequence a tap
  // generates - so those can all ask what kind of interaction they belong to. A ref rather than state:
  // nothing renders from it, and it must be readable within the same interaction, before a re-render.
  //
  // Defaults to false (mouse) and only ever flips on positive evidence. Getting this backwards is the
  // trap: `pointerType !== 'mouse'` looks equivalent but treats "unknown" as touch, which then gives
  // MOUSE users the click-toggle and closes the popover they had just hovered open. `pointerType` is
  // absent more often than it looks - jsdom does not implement PointerEvent at all.
  const isTouchInteraction = useRef(false);
  const targetId = `${dataCyPrefix}-${id}`;
  const bodyId = `${dataCyPrefix}-body-${id}`;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  }, []);

  const cancelOpen = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = undefined;
    }
  }, []);

  const open = useCallback(() => {
    cancelClose();
    setEverOpened(true);
    setIsOpen(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelOpen();
    cancelClose();
    closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose, cancelOpen]);

  // Hover-open, but only for a mouse. On a tap the browser fires mouseenter too, and letting that
  // open the popover is what would make the tap-to-toggle below close it again immediately.
  const handleMouseEnter = useCallback(() => {
    if (isTouchInteraction.current) return;
    if (!openDelay) {
      open();
      return;
    }
    cancelOpen();
    openTimer.current = setTimeout(open, openDelay);
  }, [open, openDelay, cancelOpen]);

  // Touch only. A mouse user already has hover, and adding a click toggle for them means a click after
  // hover-open reads as "clicking it closed it", which is worse than not having it at all.
  const handleClick = useCallback(() => {
    if (!isTouchInteraction.current) return;
    if (isOpen) {
      cancelClose();
      setIsOpen(false);
      return;
    }
    open();
  }, [isOpen, open, cancelClose]);

  // `touchstart` is the primary signal, not `pointerType`: it fires ONLY for touch, so its mere
  // presence is the evidence, with nothing to misread when a browser (or jsdom) does not implement
  // PointerEvent.
  const handleTouchStart = useCallback(() => {
    isTouchInteraction.current = true;
  }, []);

  // `pointerType` then refines it, and is the only thing that can flip the flag BACK. It matters on a
  // hybrid device (touchscreen laptop): once a tap has set the flag, a later mouse click would
  // otherwise still be treated as a tap. Only an explicit value is trusted - an absent one leaves the
  // last known interaction type in place. Pen reads as touch: it has no hover either.
  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (!event.pointerType) return;
    isTouchInteraction.current = event.pointerType !== 'mouse';
  }, []);

  // A pending timer must not fire against an unmounted row - claim item rows unmount freely on
  // pagination, filtering and re-validation.
  useEffect(
    () => () => {
      cancelClose();
      cancelOpen();
    },
    [cancelClose, cancelOpen],
  );

  const headerClassName = useMemo(
    // Bootstrap's warning and info are both light fills - white text on either is unreadable.
    () => `popover-header bg-${variant} ${variant === 'danger' ? 'text-white' : 'text-dark'} mb-0`,
    [variant],
  );

  if (messages.length === 0) return null;

  return (
    <>
      {/*
        Without `iconColor`, FontAwesome renders `fill: currentColor` so the glyph inherits the
        button's text colour rather than needing a hardcoded one; `small` shrinks it (FA icons size
        in em).
      */}
      <button
        type="button"
        id={targetId}
        // `small` sizes the GLYPH (FA icons size in em), so it only applies when there is one. A
        // text-only trigger is a cell value sitting alongside other cells and must not render a
        // size smaller than its neighbours.
        className={`${triggerBaseClassName} ${triggerClassName}${icon ? ' small' : ''}`}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        // `aria-controls` gives `aria-expanded` something to refer to, and `aria-describedby` is what
        // makes the content itself announced - a screen reader focusing this button previously heard
        // the label and nothing else, even though focus had just opened a popover full of text.
        //
        // BOTH are gated on `isOpen`, because the popover body only exists while open and an IDREF
        // pointing at an absent element is worse than no attribute: assistive technology drops it
        // either way, but a dangling reference reads as a wiring mistake to anyone auditing it.
        // `aria-expanded` is perfectly valid on its own, so nothing is lost while closed.
        aria-controls={isOpen ? bodyId : undefined}
        aria-describedby={isOpen ? bodyId : undefined}
        onTouchStart={handleTouchStart}
        onPointerDown={handlePointerDown}
        // PopOver closes on document `mousedown` outside its own body, and this trigger is outside it
        // (the popover portals to document.body). Without stopping propagation, a tap while open ran
        // click-away first and the toggle second - closing then reopening, so it never closed.
        onMouseDown={(event) => event.stopPropagation()}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={scheduleClose}
        data-cy={`${dataCyPrefix}-button-${id}`}
      >
        {label && <span className={icon ? 'me-1' : undefined}>{label}</span>}
        {icon && <Icon icon={icon} color={iconColor} />}
      </button>
      {everOpened && (
        <PopOver
          isOpen={isOpen}
          target={targetId}
          placement={placement}
          autoFlip
          // Enables Escape-to-close and click-away inside PopOver. Its focus trap is a no-op here
          // because the content holds no focusable elements.
          toggle={() => setIsOpen(false)}
          // role="tooltip" instead of PopOver's default role="dialog" aria-modal="true". This popover
          // opens on hover/focus and holds nothing focusable, so aria-modal was a lie with a cost: focus
          // stays on the TRIGGER, which is outside the "modal", so a screen reader collapsed the page to
          // a container the user could never enter and could not Tab past without blurring the trigger -
          // which closes it. A tooltip named by its own content, referenced via aria-describedby on the
          // trigger, is the shape this actually is.
          isModal={false}
          className="popover show bs-popover-auto popover-inner"
          // Services Australia messages are full sentences naming a care recipient, a service code and
          // a date; at the original 360 the common ones wrapped onto three or four lines. `min` keeps
          // it inside narrow viewports.
          styleOverrides={{ padding: 0, maxWidth: 'min(560px, 90vw)' }}
        >
          {/*
          A plain grouping wrapper - no `role`/`aria-label` of its own, which would compete with the
          PopOver container's own role.
        */}
          <div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
            <h3 className={headerClassName}>{header}</h3>
            {/* `id` (not just data-cy) because the trigger's aria-describedby points here - this is the
              element whose text should be announced when the trigger takes focus. */}
            <div id={bodyId} className="popover-body text-start" data-cy={bodyId}>
              {/*
              One message per line. Services Australia can return several distinct validation
              failures for a single item, and joining them onto one line is what makes those
              unreadable - hence parseSahApiErrors returning a list rather than a joined string.
            */}
              {messages.map((message, index) => (
                <div key={message} className={index > 0 ? 'mt-2' : undefined}>
                  {message}
                </div>
              ))}
            </div>
          </div>
        </PopOver>
      )}
    </>
  );
};

export default HoverPopover;
